import React, { forwardRef, useRef, useImperativeHandle, useEffect } from "react";
import html2canvas from "html2canvas";

// Standard Bender Extender logo (matches RecapCard)
// Faces-only white logo (baked shadow layer stripped) — no CSS filter needed,
// renders identically in the live preview and the captured PNG.
const LOGO_PATH = "/bender-logo-white.png";

const BenderSummaryCard = forwardRef(({ drinks, drinkType, duration }, ref) => {
  const logoLoadedRef = useRef(false);
  const containerRef = useRef(null);

  useEffect(() => {
    // Preload Logo
    const img = new Image();
    img.decoding = "sync";
    img.crossOrigin = "anonymous";
    img.onload = () => { logoLoadedRef.current = true; };
    img.onerror = () => { logoLoadedRef.current = true; };
    img.src = LOGO_PATH;
  }, []);

  useImperativeHandle(ref, () => ({
    generateImage: async () => {
      if (!containerRef.current) return null;

      // Robust Wait for Logo & Fonts
      let waited = 0;
      while (!logoLoadedRef.current && waited < 3000) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        waited += 100;
      }
      await document.fonts.ready;
      await new Promise((resolve) => setTimeout(resolve, 300));

      try {
        const canvas = await html2canvas(containerRef.current, {
          backgroundColor: null, // Transparent background
          scale: 3,
          useCORS: true,
          logging: false,
          onclone: (doc) => {
             const el = doc.getElementById('bender-summary-capture');
             if(el) { el.style.background = 'transparent'; }
          }
        });
        return canvas.toDataURL('image/png');
      } catch (error) {
        console.error("Image generation failed:", error);
        return null;
      }
    },
  }));

  return (
    <div
      ref={containerRef}
      id="bender-summary-capture"
      className="relative w-[320px] flex flex-col items-center gap-4 p-4 pt-8 pb-8 bg-transparent transform-gpu overflow-hidden"
      style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}
    >
      {/* TITLE (matches RecapCard) */}
      <div className="text-center">
        <h1 className="text-3xl font-black italic text-white tracking-tighter uppercase drop-shadow-md leading-none">
          BENDERQUEST
        </h1>
        <p className="text-[10px] font-bold text-white uppercase tracking-[0.4em] mt-1 drop-shadow-sm">
          OFFICIAL RECAP
        </p>
      </div>

      {/* STATS (RecapCard styling) */}
      <div className="flex flex-col items-center gap-3 w-full">
        <div className="flex flex-col items-center">
          <span className="text-xs font-bold text-white/70 uppercase tracking-widest mb-0.5">DRINKS</span>
          <span className="text-3xl font-black text-white italic leading-none">{drinks}</span>
        </div>
        <div className="flex flex-col items-center">
          <span className="text-xs font-bold text-white/70 uppercase tracking-widest mb-0.5">BOOZE</span>
          <span className="text-3xl font-black text-white italic leading-none uppercase text-center max-w-[280px]">{drinkType || "—"}</span>
        </div>
        <div className="flex flex-col items-center">
          <span className="text-xs font-bold text-white/70 uppercase tracking-widest mb-0.5">DURATION</span>
          <span className="text-3xl font-black text-white italic leading-none">{duration}</span>
        </div>
      </div>

      {/* LOGO (standard, white — matches RecapCard) */}
      <div className="mt-2">
        <img
          src={LOGO_PATH}
          alt="Bender Extender"
          decoding="sync"
          crossOrigin="anonymous"
          className="w-24 h-auto object-contain"
          style={{ display: "block" }}
        />
      </div>
    </div>
  );
});

BenderSummaryCard.displayName = "BenderSummaryCard";

export default BenderSummaryCard;
